import { template as template_fd9b2463e5f141cfb5666b64daa1f11a } from "@ember/template-compiler";
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { concat } from '@ember/helper';
import { fn } from '@ember/helper';
import tElse from 'volta/helpers/t-else';
import { sanitized } from 'volta/utils/text-utils';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import BvInline from 'volta/components/bv-inline';
import BvIcon from 'volta/components/bv-icon';
import BvTag from 'volta/components/bv-tag';
import BvTextField from 'volta/components/bv-text-field';
import BvBtnGroup from 'volta/components/bv-btn-group';
import BvEmptySearchResult from 'volta/components/bv-empty-search-result';
import BvDndHandle from '../bv-dnd/handle';
import orElse from 'volta/helpers/or-else';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import gt from 'ember-truth-helpers/helpers/gt';
import t from 'ember-intl/helpers/t';
import set from 'ember-set-helper/helpers/set';
import isPresent from 'volta/helpers/is-present';
import lowerCase from 'lodash/lowerCase';
import type { TCollectionPropertyForUI } from 'volta/models/types/collection-view';
interface CollectionViewPropertiesSignature {
    Args: {
        properties: TCollectionPropertyForUI[];
        groupBy?: string[];
        onChange?: (props: TCollectionPropertyForUI[]) => void;
    };
    Element: HTMLElement;
}
export default class CollectionViewPropertiesComponent extends Component<CollectionViewPropertiesSignature> {
    @tracked
    search?: string;
    groupName = `CollectionViewProperties_${guidFor(this)}`;
    get displayedProperties() {
        const toCheck = new Set(this.args.groupBy ?? []);
        return this.args.properties.filter((p)=>!p.isStatic && !toCheck.has(p.property));
    }
    get filteredProperties() {
        const { search } = this;
        if (!search || search === '') {
            return this.displayedProperties;
        }
        const _search = sanitized(search);
        return this.displayedProperties.filter((p)=>p.title && sanitized(p.title).includes(_search));
    }
    toggleProperty = (item: TCollectionPropertyForUI)=>{
        const newProps = this.args.properties.reduce<TCollectionPropertyForUI[]>((acc, prop)=>{
            const isDisabled = item.property === prop.property ? !prop.disabled : prop.disabled;
            if (!isDisabled) {
                acc.push(prop);
            }
            return acc;
        }, []);
        this.args.onChange?.(newProps);
    };
    toggleIsFixed = (item: TCollectionPropertyForUI)=>{
        const newProps = this.args.properties.reduce<TCollectionPropertyForUI[]>((acc, prop)=>{
            if (!prop.disabled) {
                const isFixed = item.property === prop.property ? (prop.isFixed ? undefined : 'left') : prop.isFixed;
                acc.push({
                    ...prop,
                    isFixed
                });
            }
            return acc;
        }, []);
        this.args.onChange?.(newProps.sort((a, b)=>{
            return Number(!!b.isFixed) - Number(!!a.isFixed);
        }));
    };
    reorderProperties = (properties: TCollectionPropertyForUI[])=>{
        const newProps = properties.reduce<TCollectionPropertyForUI[]>((acc, prop)=>{
            if (!prop.disabled) {
                acc.push(prop);
            }
            return acc;
        }, []);
        const reorderedSet = new Set(newProps.map((p)=>p.property));
        const propsWithoutReordered = this.args.properties.filter((p)=>!reorderedSet.has(p.property) && !p.disabled);
        this.args.onChange?.(newProps.concat(propsWithoutReordered));
    };
    static{
        template_fd9b2463e5f141cfb5666b64daa1f11a(`
    <div class='collection-view-properties' ...attributes>
      <div class='u-p-1'>
        <BvTextField
          class='u-flex-fill'
          @labelHidden={{true}}
          @placeholder={{t 'search'}}
          @value={{this.search}}
          @prefix={{component BvIcon glyph='search'}}
          @onChange={{set this 'search'}}
          @autoFocus={{true}}
          @clearButton={{gt this.search.length 0}}
          @onClearButtonClick={{set this 'search' undefined}}
        />
      </div>
      <div {{sortableGroup groupName=this.groupName onChange=this.reorderProperties direction='y'}}>
        {{#each this.filteredProperties key='property' as |option|}}
          {{#let
            (or option.disabled (isPresent option.isFixed) (isPresent this.search))
            as |disabled|
          }}
            <BvInline
              @blockAlign='center'
              @gap='1'
              @wrap={{false}}
              class='collection-view-property u-py-1 u-px-1 u-bg-surface'
              disabled={{disabled}}
              data-test-name={{option.property}}
              {{sortableItem groupName=this.groupName model=option disabled=disabled}}
            >
              <BvDndHandle @disabled={{disabled}} />
              <BvInline
                @blockAlign='center'
                @gap='2'
                @align='space-between'
                @wrap={{false}}
                class='u-flex-grow-1 u-overflow-hidden'
              >
                <BvTag
                  @text={{tElse (orElse option.title option.property)}}
                  @color={{option.color}}
                  @size='sm'
                />
                <BvBtnGroup @size='sm' @subtle={{true}} @segmented={{false}} as |group|>
                  {{#unless option.disabled}}
                    <group.btn
                      @iconLeft='lock-closed'
                      @iconLeftColor={{unless option.isFixed 'icon-disabled'}}
                      @onClick={{fn this.toggleIsFixed option}}
                      @testName={{concat 'fixed-prop-' option.property}}
                    />
                  {{/unless}}
                  <group.btn
                    @iconLeft={{if option.disabled 'eye-off' 'eye'}}
                    @iconLeftColor={{if option.disabled 'icon-disabled'}}
                    @onClick={{fn this.toggleProperty option}}
                    @outline={{not disabled}}
                    @testName={{concat 'disable-prop-' option.property}}
                  />
                </BvBtnGroup>
              </BvInline>
            </BvInline>
          {{/let}}
        {{else}}
          <BvEmptySearchResult
            @title={{t
              'bvList.emptySearchResultTitle'
              resourceNamePlural=(lowerCase (t 'properties'))
            }}
          />
        {{/each}}
      </div>
    </div>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
declare module '@glint/environment-ember-loose/registry' {
    export default interface Registry {
        'CollectionView::Properties': typeof CollectionViewPropertiesComponent;
        'collection-view/properties': typeof CollectionViewPropertiesComponent;
    }
}
